import React, { useMemo, useState, useEffect } from 'react';
import {
  BulbOutlined,
  IdcardOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
} from '@ant-design/icons';

const PALETTES = [
  ['#5B8DEF', '#3D5AFE'],
  ['#36D1A6', '#1FA97A'],
  ['#F7B733', '#E8860C'],
  ['#A18CD1', '#7B5EA7'],
  ['#4FACFE', '#00A3D9'],
  ['#43E97B', '#2BB673'],
  ['#FA709A', '#E84A7A'],
  ['#30CFD0', '#1A9E9F'],
];

const hashSeed = (seed) => {
  let hash = 0;
  const str = String(seed || '');
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const resolveGlyph = (agent) => {
  const haystack = [agent?.name, agent?.role, agent?.intro]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/课程|course|答疑|qa|question/.test(haystack)) {
    return { kind: 'icon', Icon: QuestionCircleOutlined };
  }
  if (/签证|visa|报考|application/.test(haystack)) {
    return { kind: 'icon', Icon: IdcardOutlined };
  }
  if (/作品|创作|creative|portfolio|guidance|art/.test(haystack)) {
    return { kind: 'icon', Icon: BulbOutlined };
  }
  if (/课后|after|service|class/.test(haystack)) {
    return { kind: 'icon', Icon: ReadOutlined };
  }

  const name = (agent?.name || 'A').trim();
  const initial = name ? name.charAt(0).toUpperCase() : 'A';
  return { kind: 'initial', text: initial };
};

const AgentAvatar = ({
  agent,
  size = 32,
  disabled = false,
  selected = false,
  style = {},
}) => {
  const avatarUrl = (agent?.avatar || '').trim();
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [avatarUrl]);

  const seed = `${agent?.id || ''}-${agent?.name || ''}`;
  const palette = PALETTES[hashSeed(seed) % PALETTES.length];
  const glyph = useMemo(() => resolveGlyph(agent), [agent]);

  const ringStyle = selected
    ? { boxShadow: `0 0 0 2px #fff, 0 0 0 3px ${palette[0]}` }
    : { boxShadow: '0 1px 4px rgba(0,0,0,0.08)' };

  const [from, to] = palette;
  const fontSize = glyph.kind === 'icon' ? Math.round(size * 0.46) : Math.round(size * 0.42);
  const IconComponent = glyph.kind === 'icon' ? glyph.Icon : null;

  if (avatarUrl && !imageFailed) {
    return (
      <img
        src={avatarUrl}
        alt={agent?.name || 'agent'}
        onError={() => setImageFailed(true)}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
          opacity: disabled ? 0.45 : 1,
          filter: disabled ? 'grayscale(0.85)' : 'none',
          ...ringStyle,
          ...style,
        }}
      />
    );
  }

  return (
    <div
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize,
        fontWeight: 600,
        letterSpacing: '0.02em',
        flexShrink: 0,
        opacity: disabled ? 0.45 : 1,
        userSelect: 'none',
        ...ringStyle,
        ...style,
      }}
    >
      {IconComponent ? (
        <IconComponent style={{ fontSize, color: '#fff' }} />
      ) : (
        glyph.text
      )}
    </div>
  );
};

export default AgentAvatar;
